import { useMemo, useRef, useState } from 'react';
import { TriangleAlertIcon } from 'lucide-react';

import { Textarea } from '@/components/ui/textarea';
import type { VariableManifest } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import { useVariableManifest } from '../hooks/useVariableManifest';

interface ActiveExpression {
  /** Where the currently-typed dot-segment starts — the only span a
   *  selected suggestion ever replaces (contracts/template-variable-
   *  manifest-api.md: "replacing only the partial segment being typed"). */
  segmentStart: number;
  /** The partial expression's dot-separated parts, e.g. `{{ signal.task.t`
   *  under the cursor is `['signal', 'task', 't']`. Leading whitespace
   *  right after `{{` is stripped before splitting. */
  parts: string[];
}

/**
 * Finds the `{{ ... }}` expression, if any, that the cursor currently sits
 * inside — the nearest unclosed `{{` before it. Returns `null` once that
 * expression has already been closed with `}}` before the cursor, or if
 * there is no open expression at all (spec 074, US3: autocomplete only ever
 * applies inside an in-progress template expression).
 */
function findActiveExpression(
  value: string,
  cursor: number,
): ActiveExpression | null {
  const openIndex = value.slice(0, cursor).lastIndexOf('{{');
  if (openIndex === -1) return null;
  const between = value.slice(openIndex + 2, cursor);
  if (between.includes('}}')) return null;

  const trimmed = between.replace(/^\s+/, '');
  const parts = trimmed.split('.');
  const lastPart = parts[parts.length - 1] ?? '';
  return { segmentStart: cursor - lastPart.length, parts };
}

interface Suggestion {
  text: string;
  description?: string | null;
}

/**
 * Walks the fetched manifest tree level-by-level (root → group → field)
 * against the partial expression's parts — entirely client-side, no network
 * call per keystroke (contracts/template-variable-manifest-api.md's
 * frontend contract). Matching is a simple case-sensitive prefix filter at
 * whichever level the cursor's partial expression currently sits at.
 */
function computeSuggestions(
  parts: string[],
  manifest: VariableManifest | undefined,
): Suggestion[] {
  if (!manifest) return [];

  // Root: only `signal` is ever suggested. Also covers `parts.length > 1`
  // with a `parts[0]` that plainly isn't (a prefix of) `signal` at all —
  // nothing narrower can ever match, so no suggestions at any level.
  if (!'signal'.startsWith(parts[0] ?? '')) return [];
  if (parts.length === 1) return [{ text: 'signal', description: null }];

  // Group: `{{ signal.<prefix>`.
  const groupPrefix = parts[1] ?? '';
  if (parts.length === 2) {
    return manifest.groups
      .filter((group) => group.name.startsWith(groupPrefix))
      .map((group) => ({ text: group.name, description: null }));
  }

  // Field: `{{ signal.<group>.<prefix>` — only once `<group>` names one of
  // the manifest's own groups exactly; a group that doesn't (yet) match
  // anything real has no fields to suggest.
  const group = manifest.groups.find((g) => g.name === groupPrefix);
  if (!group || parts.length !== 3) return [];
  const fieldPrefix = parts[2] ?? '';
  return group.fields
    .filter((field) => field.name.startsWith(fieldPrefix))
    .map((field) => ({ text: field.name, description: field.description }));
}

/** A simple, well-formed `{{ <dot-path> }}` expression — one identifier,
 *  optionally dotted, no other characters. Deliberately looser than the
 *  backend's real Jinja-based `validate_template_syntax` (spec 074, US4):
 *  this is the *advisory*, client-side-only manifest-membership check
 *  (research.md D-MAN2), never the blocking syntax rule, so it only needs
 *  to recognize the paths worth checking against the manifest — a
 *  genuinely malformed expression is caught at save time by the backend,
 *  not here. */
const COMPLETED_DOT_PATH_EXPRESSION =
  /\{\{\s*([A-Za-z_][A-Za-z0-9_.]*)\s*\}\}/g;

/**
 * Every completed (already closed with `}}`) simple dot-path expression in
 * `value`, in source order — duplicates kept, since each occurrence gets its
 * own warning.
 */
function findCompletedPaths(value: string): string[] {
  return [...value.matchAll(COMPLETED_DOT_PATH_EXPRESSION)]
    .map((match) => match[1])
    .filter((path): path is string => path !== undefined);
}

/**
 * Whether `path` (e.g. `signal.merge_request.url`) resolves to a real
 * manifest field. Anything this check cannot confidently evaluate — no
 * manifest loaded yet, a non-`signal` root (the blocking syntax check's own
 * concern), or a path that isn't a full `signal.<group>.<field>` leaf — is
 * treated as present rather than flagged, so this never produces a false
 * warning for something outside its own scope.
 */
function isPathInManifest(
  path: string,
  manifest: VariableManifest | undefined,
): boolean {
  if (!manifest) return true;
  const parts = path.split('.');
  if (parts[0] !== 'signal' || parts.length !== 3) return true;
  const group = manifest.groups.find((g) => g.name === parts[1]);
  if (!group) return false;
  return group.fields.some((field) => field.name === parts[2]);
}

export interface SignalTemplateFieldProps {
  id?: string;
  workspaceId: string;
  /** Which driver's manifest to fetch and walk — a `TriggerProfile.type`. */
  driverType: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
}

/**
 * A plain `Textarea` for a `{{ signal.* }}` template field, augmented with
 * incremental, keystroke-level autocomplete (spec 074, US3, FR-009) — opens
 * as soon as `{{` is typed, with no further characters required, and
 * narrows on every keystroke by walking the driver's manifest (fetched once,
 * via `useVariableManifest`) entirely client-side.
 *
 * Selecting a suggestion replaces only the partial dot-segment under the
 * cursor (`{{ signal.task.t` + selecting `title` → `{{ signal.task.title`),
 * never the whole expression — so a person can keep narrowing group-by-group
 * without retyping anything already confirmed.
 *
 * Also renders a non-blocking warning below the field for any completed
 * (`}}`-closed) expression whose path is well-formed but not present in the
 * manifest (spec 074, US4, research.md D-MAN2) — e.g. `{{ signal.merge_
 * request.url }}` typed into a `workspace_task_change` profile's field. This
 * is advisory only: it never disables typing or blocks a save; the backend's
 * own blocking syntax rule (malformed `{{ }}`, non-`signal` root) is a
 * separate, save-time check surfaced elsewhere (`TriggerProfileFormSheet`'s
 * server-error banner), not by this component.
 */
export function SignalTemplateField({
  id,
  workspaceId,
  driverType,
  value,
  onChange,
  disabled,
  placeholder,
  rows,
  className,
  ...rest
}: SignalTemplateFieldProps) {
  const { manifest } = useVariableManifest(workspaceId, driverType);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const active = cursor === null ? null : findActiveExpression(value, cursor);
  const suggestions = active ? computeSuggestions(active.parts, manifest) : [];
  const open = active !== null && suggestions.length > 0;

  // Recomputed from `value` alone (not `cursor`) — every completed
  // expression is checked regardless of where the caret currently sits,
  // entirely client-side against the already-fetched manifest (no request
  // added, research.md D-MAN2).
  const unknownPaths = useMemo(() => {
    const seen = new Set<string>();
    return findCompletedPaths(value).filter(
      (path) =>
        !isPathInManifest(path, manifest) && !seen.has(path) && seen.add(path),
    );
  }, [value, manifest]);

  function syncCursor(target: HTMLTextAreaElement) {
    setCursor(target.selectionStart);
  }

  function selectSuggestion(text: string) {
    if (!active || cursor === null) return;
    const next =
      value.slice(0, active.segmentStart) + text + value.slice(cursor);
    const nextCursor = active.segmentStart + text.length;
    onChange(next);
    setCursor(nextCursor);
    // The DOM textarea hasn't repainted with `next` yet on this synchronous
    // handler (it's still showing the pre-selection value) — defer the
    // refocus/caret move to after React commits the new value.
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  return (
    <div className="relative">
      <Textarea
        id={id}
        ref={textareaRef}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        className={cn('font-mono', className)}
        onChange={(event) => {
          onChange(event.target.value);
          syncCursor(event.target);
        }}
        onClick={(event) => {
          syncCursor(event.currentTarget);
        }}
        onKeyUp={(event) => {
          syncCursor(event.currentTarget);
        }}
        onBlur={() => {
          setCursor(null);
        }}
        {...rest}
      />
      {open ? (
        <ul
          role="listbox"
          aria-label="Template variable suggestions"
          className="border-border bg-popover absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border p-1 text-sm shadow-floating"
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.text}>
              <button
                type="button"
                role="option"
                className="hover:bg-muted w-full rounded-sm px-2 py-1 text-left font-mono"
                // Keeps the textarea focused (and `cursor` set) through the
                // click below — a default mousedown would blur it first.
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  selectSuggestion(suggestion.text);
                }}
              >
                {suggestion.text}
                {suggestion.description ? (
                  <span className="text-muted-foreground ml-2 font-sans text-xs">
                    {suggestion.description}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {unknownPaths.length > 0 ? (
        <p
          data-variant="advisory-warning"
          className="text-warning mt-1 flex items-start gap-1 text-xs"
        >
          <TriangleAlertIcon
            aria-hidden="true"
            className="mt-0.5 size-3 shrink-0"
          />
          <span>
            Not in this driver&apos;s variable list: {unknownPaths.join(', ')}.
            It may still render as empty at run time.
          </span>
        </p>
      ) : null}
    </div>
  );
}
